/**
 * Commission Configuration (UI-2 v3 layout, BE-6a data)
 *
 * Two sections: Default Rules (all customers) + Customer Overrides, and the Add Rule dialog.
 * Each rule is one recipient's rate; at finalisation the internal-task assignee earns at their most specific rule:
 * customer + service → service → customer → their default. Only the TC's own (internal) services earn commission.
 * Route: /accounting/commissions/config
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  IconButton, Tooltip, Switch, FormControlLabel, Snackbar,
} from '@mui/material';
import { Add, ArrowBack, Delete, Edit } from '@mui/icons-material';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { DataTable, StatusChip } from '../../components/accounting';
import { can } from '../../utils/roleUtils';
import ApiService from '../../services/api.service';
import { commissionService, RECIPIENT_TYPES } from '../../services/accounting/commission.service';
import { keysSnakeToCamel, serverMessage } from '../../utils/apiConvert';

const sessionUser = () => {
  try { return JSON.parse(localStorage.getItem('currentUser') || '{}'); } catch (e) { return {}; }
};

const EMPTY = { internalOrganizationId: '', internalServiceId: '', recipientId: '', rateType: 'PERCENTAGE', rateValue: '', active: true };
const fieldSx = { InputProps: { sx: { fontSize: fs.sm } }, InputLabelProps: { sx: { fontSize: fs.sm } } };

export const ruleScope = (r) => `${r.internalOrganization?.name || 'All customers'} · ${r.internalServiceName || 'All services'}`;

const CommissionConfig = () => {
  const navigate = useNavigate();
  const user = sessionUser();
  const tcId = user.typingCenterId || user.typing_center_id;
  const canWrite = can(user, 'finance', 'write');
  const [rules, setRules] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([commissionService.getRules(), commissionService.getRecipients()]);
      setRules(r);
      setRecipients(a);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load config'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => {
    if (!tcId) return;
    ApiService.getInternalOrganizations(tcId).then((res) => setOrganizations(keysSnakeToCamel(res?.data?.data || []))).catch(() => {});
    ApiService.get('/api/internal-services', { params: { typingCenterId: tcId } })
      .then((res) => setServices(keysSnakeToCamel(res?.data?.data || []))).catch(() => {});
  }, [tcId]);

  const defaultRules = rules.filter((r) => !r.internalOrganization);
  const overrideRules = rules.filter((r) => r.internalOrganization);

  const openNew = () => { setForm(EMPTY); setEditing({}); };
  const openEdit = (r) => {
    setForm({
      internalOrganizationId: r.internalOrganization?.id || '', internalServiceId: r.internalServiceId || '',
      recipientId: r.recipient?.id || '', rateType: r.rateType, rateValue: String(r.rateValue), active: r.active !== false,
    });
    setEditing(r);
  };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete ${r.recipient?.name}'s rule for ${ruleScope(r)}?`)) return;
    try {
      await commissionService.deleteRule(r.id);
      setSnackbar('Rule deleted');
      loadData();
    } catch (err) {
      setError(serverMessage(err, 'Failed to delete'));
    }
  };

  const handleSave = async () => {
    setBusy(true);
    setError(null);
    const data = {
      internalOrganizationId: form.internalOrganizationId || null, internalServiceId: form.internalServiceId || null,
      recipientId: form.recipientId, rateType: form.rateType, rateValue: form.rateValue,
    };
    try {
      if (editing?.id) await commissionService.updateRule(editing.id, { ...data, active: form.active });
      else await commissionService.createRule(data);
      setSnackbar(editing?.id ? 'Rule updated' : 'Rule added');
      setEditing(null);
      loadData();
    } catch (err) {
      setError(serverMessage(err, 'Failed to save the rule'));
    } finally {
      setBusy(false);
    }
  };

  const actions = { key: 'actions', label: '', width: '9%', align: 'right', render: (r) => canWrite && (
    <Box display="flex" justifyContent="flex-end">
      <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(r)}><Edit fontSize="small" /></IconButton></Tooltip>
      <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(r)}><Delete fontSize="small" /></IconButton></Tooltip>
    </Box>
  ) };
  const COLUMNS_COMMON = [
    { key: 'recipient', label: 'Recipient', width: '20%', render: (r) => r.recipient?.name || '—' },
    { key: 'rateType', label: 'Rate Type', width: '12%', render: (r) => (r.rateType === 'PERCENTAGE' ? 'Percentage' : 'Fixed') },
    { key: 'rateValue', label: 'Rate', width: '13%', render: (r) => (r.rateType === 'PERCENTAGE' ? `${Number(r.rateValue)}% of SC` : `AED ${Number(r.rateValue).toFixed(2)}`) },
    { key: 'basedOn', label: 'Based On', width: '13%', render: () => 'Service Charge' },
    { key: 'active', label: 'Status', width: '9%', render: (r) => <StatusChip tone={r.active !== false ? 'success' : 'default'} label={r.active !== false ? 'Active' : 'Inactive'} /> },
  ];
  const COLUMNS_DEFAULT = [
    COLUMNS_COMMON[0],
    { key: 'service', label: 'Service', width: '20%', render: (r) => (r.internalServiceName
      ? r.internalServiceName
      : <Typography component="span" sx={{ fontSize: fs.xs, color: 'text.secondary' }}>All customers · All services</Typography>) },
    ...COLUMNS_COMMON.slice(1), actions,
  ];
  const COLUMNS_OVERRIDE = [
    { key: 'organization', label: 'Customer', width: '16%', render: (r) => r.internalOrganization?.name },
    { key: 'service', label: 'Service', width: '16%', render: (r) => r.internalServiceName || 'All services' },
    ...COLUMNS_COMMON, actions,
  ];

  const valid = form.recipientId && Number(form.rateValue) > 0 && /^\d+(\.\d{1,2})?$/.test(String(form.rateValue))
    && (form.rateType !== 'PERCENTAGE' || Number(form.rateValue) <= 100);

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate('/accounting/commissions')}><ArrowBack fontSize="small" /></IconButton>
          <Typography variant="h5" fontWeight={600} color="secondary.main">Commission Configuration</Typography>
        </Box>
        {canWrite && (
          <Button variant="contained" size="small" startIcon={<Add />} onClick={openNew} sx={{ textTransform: 'none', fontSize: fs.sm }}>
            Add Rule
          </Button>
        )}
      </Box>

      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mb: 1 }}>Default Rules (all customers)</Typography>
      <DataTable columns={COLUMNS_DEFAULT} data={defaultRules} loading={loading} pagination={false}
        emptyMessage="No default rules configured." />

      <Typography sx={{ fontSize: fs.sm, fontWeight: fw.semibold, mt: 3, mb: 1 }}>Customer Overrides</Typography>
      <DataTable columns={COLUMNS_OVERRIDE} data={overrideRules} loading={loading} pagination={false}
        emptyMessage="No customer overrides configured." />

      <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mt: 2, fontStyle: 'italic' }}>
        Priority for each recipient: Customer + Service &gt; Service &gt; Customer &gt; their default. Only the recipient
        assigned to the internal task earns, and only on the TC&apos;s own services.
      </Typography>

      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: fs.md, fontWeight: fw.semibold }}>{editing?.id ? 'Edit Commission Rule' : 'Add Commission Rule'}</DialogTitle>
        <DialogContent dividers>
          <TextField select label="Customer (optional)" fullWidth size="small" sx={{ mt: 1, mb: 2 }} {...fieldSx}
            value={form.internalOrganizationId} onChange={set('internalOrganizationId')}>
            <MenuItem value="" sx={{ fontSize: fs.sm }}><em>All customers (default)</em></MenuItem>
            {organizations.map((o) => <MenuItem key={o.id} value={o.id} sx={{ fontSize: fs.sm }}>{o.name}</MenuItem>)}
          </TextField>
          <TextField select label="Service (optional)" fullWidth size="small" sx={{ mb: 2 }} {...fieldSx}
            value={form.internalServiceId} onChange={set('internalServiceId')}>
            <MenuItem value="" sx={{ fontSize: fs.sm }}><em>All services</em></MenuItem>
            {services.map((s) => <MenuItem key={s.id} value={s.id} sx={{ fontSize: fs.sm }}>{s.name || s.serviceName}</MenuItem>)}
          </TextField>
          <TextField select label="Recipient" fullWidth size="small" sx={{ mb: 2 }} required {...fieldSx}
            value={form.recipientId} onChange={set('recipientId')} disabled={Boolean(editing?.id)}>
            {recipients.map((r) => (
              <MenuItem key={r.id} value={r.id} sx={{ fontSize: fs.sm }}>{r.name} ({RECIPIENT_TYPES[r.type] || r.type})</MenuItem>
            ))}
          </TextField>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField select label="Rate Type" size="small" sx={{ flex: 1 }} {...fieldSx} value={form.rateType} onChange={set('rateType')}>
              <MenuItem value="PERCENTAGE" sx={{ fontSize: fs.sm }}>Percentage</MenuItem>
              <MenuItem value="FIXED" sx={{ fontSize: fs.sm }}>Fixed Amount</MenuItem>
            </TextField>
            <TextField label={form.rateType === 'PERCENTAGE' ? 'Rate (%)' : 'Amount (AED)'} size="small" sx={{ flex: 1 }} type="number" required
              {...fieldSx} inputProps={{ min: 0, step: '0.01' }} value={form.rateValue} onChange={set('rateValue')} />
          </Box>
          {editing?.id && (
            <FormControlLabel sx={{ mt: 1 }} label={<Typography sx={{ fontSize: fs.sm }}>Active</Typography>}
              control={<Switch size="small" checked={form.active} onChange={set('active')} />} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={busy || !valid} sx={{ textTransform: 'none' }}>
            {editing?.id ? 'Save' : 'Add Rule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={3000} onClose={() => setSnackbar(null)} message={snackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
};

export default CommissionConfig;
